import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import seacureLogo from '@/assets/SEACURE(CYBER SECURITY).png'

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      showError(t('errors.missingFields'), 'Please enter your email address')
      return
    }

    setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      setEmailSent(true)
      success(t('notifications.success'), 'Password reset email sent! Check your inbox.')
    } catch (err: any) {
      console.error('Error sending reset email:', err)
      showError(t('errors.operationFailed'), err.message || 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* back button */}
        <div className="mb-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        {/* logo */}
        <div className="text-center mb-8">
          <img src={seacureLogo} alt="Seacure" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">SeacureDB</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">Maritime Cybersecurity Asset Management</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-center">
              {emailSent ? 'Check Your Email' : 'Forgot Password?'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailSent ? (
              <div className="text-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
                <div>
                  <p className="text-slate-700 dark:text-slate-300 mb-2">
                    Password reset instructions have been sent to:
                  </p>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {email}
                  </p>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600 mx-auto mb-2" />
                  <p className="mb-2">Please check your email inbox (and spam folder).</p>
                  <p>Click the link in the email to reset your password.</p>
                  <p className="mt-2 text-xs">The link will expire in 1 hour.</p>
                </div>
                <div className="space-y-2">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full"
                    variant="default"
                  >
                    Back to Login
                  </Button>
                  <Button
                    onClick={() => {
                      setEmailSent(false)
                      setEmail('')
                    }}
                    className="w-full"
                    variant="outline"
                  >
                    Resend Email
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  Enter your email address and we'll send you instructions to reset your password.
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('auth.email')}
                  </label>
                  <Input
                    type="email"
                    placeholder="your.email@sea-net.co.kr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Instructions'}
                </Button>

                <div className="text-center">
                  <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-slate-600 dark:text-slate-400">
          <p>Protected by enterprise-grade security</p>
        </div>
      </div>
    </div>
  )
}

